import { useEffect, useState } from "react";
import { subscribeLegalPage } from "../lib/legalPages";
import { Container } from "./ui";
import Navbar from "./Navbar";
import Footer from "./Footer";

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", { dateStyle: "long" });
}

// Renders bold-only inline markup: **text** -> <strong>.
function renderInline(text, key) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <span key={key}>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i} className="font-bold text-navy">
            {part.slice(2, -2)}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

// Minimal markdown-lite renderer: "## " headings, "- " list items, blank-line
// paragraphs, **bold** inline. Deliberately not a full markdown parser — the
// admin editor only ever produces this small subset.
function renderContent(content) {
  const lines = content.split("\n");
  const blocks = [];
  let paragraph = [];
  let list = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: "p", text: paragraph.join(" ") });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push({ type: "ul", items: list });
      list = [];
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h2", text: line.slice(3) });
    } else if (line.startsWith("- ")) {
      flushParagraph();
      list.push(line.slice(2));
    } else if (line === "") {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();

  return blocks.map((block, i) => {
    if (block.type === "h2") {
      return (
        <h2 key={i} className="mt-8 text-lg font-extrabold text-navy first:mt-0">
          {renderInline(block.text, i)}
        </h2>
      );
    }
    if (block.type === "ul") {
      return (
        <ul key={i} className="mt-3 list-disc space-y-1.5 pl-5 text-navy/70">
          {block.items.map((item, j) => (
            <li key={j}>{renderInline(item, j)}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="mt-3 leading-relaxed text-navy/70">
        {renderInline(block.text, i)}
      </p>
    );
  });
}

export default function LegalPage({ slug }) {
  const [page, setPage] = useState(undefined);

  useEffect(() => subscribeLegalPage(slug, setPage), [slug]);

  return (
    <div className="overflow-x-hidden">
      <Navbar />
      <main className="min-h-[60vh] pt-28 pb-20">
        <Container className="max-w-3xl">
          {page === undefined ? (
            <p className="text-navy/40">Loading…</p>
          ) : !page || page.status !== "published" ? (
            <div className="rounded-2xl border border-navy/10 bg-[var(--color-bg-soft)] px-6 py-10 text-center">
              <h1 className="text-xl font-extrabold text-navy">This page isn't published yet</h1>
              <p className="mt-2 text-navy/50">Please check back soon.</p>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-extrabold text-navy sm:text-4xl">{page.title}</h1>
              <p className="mt-2 text-sm font-semibold text-navy/40">
                Version {page.version}
                {formatDate(page.effectiveDate) && ` · Effective ${formatDate(page.effectiveDate)}`}
              </p>
              <div className="mt-6">{renderContent(page.content)}</div>
            </>
          )}
        </Container>
      </main>
      <Footer />
    </div>
  );
}
